// owner/screens/Bookings.js — «الحجوزات» (/bookings), the approved mock-up (owner-app-mockups/bookings-phone.html)
// with Adil's 4 Nov changes: every booking, latest check-in first; the heading is the server's total for the current
// filter (not "N in November"); no «تصفية» button — the overview's unit picker instead, when the owner has more
// than one unit (200+ units must be able to narrow by unit). Unit photos come from the cached units list.

import React, { useMemo, useState } from 'react';
import useLanguage from '../../hooks/useLanguage';
import { joinList } from '../../utils/localeText';
import UnitPicker from '../ui/UnitPicker';
import { SkeletonBlocks, ErrorState, EmptyState } from '../ui/States';
import useOwnerFormat from '../ui/useOwnerFormat';
import { useOwnerUnits } from '../api/ownerReads';
import { useOwnerBookings, BookingFilters, BookingRows } from './bookings/BookingsList';

const Bookings = () => {
  const { t, language } = useLanguage();
  const f = useOwnerFormat();
  const units = useOwnerUnits();
  const [unitId, setUnitId] = useState('');  // '' = all units
  const state = useOwnerBookings(unitId);

  const unitList = useMemo(() => (Array.isArray(units.data) ? units.data : []), [units.data]);
  const photoById = useMemo(
    () => Object.fromEntries(unitList.map((u) => [String(u._id), u.bookingPageSettings?.primaryPhoto])),
    [unitList]
  );

  if (units.isLoading) return <SkeletonBlocks blocks={3} />;
  if (units.isError) return <ErrorState title={t('ownerApp.overview.loadError')} onRetry={units.refetch} />;
  if (unitList.length === 0) {
    return <EmptyState icon="units" title={t('ownerApp.overview.noUnitsTitle')} hint={t('ownerApp.overview.noUnitsHint')} />;
  }

  const scope = unitId ? unitList.find((u) => String(u._id) === unitId)?.name : t('ownerApp.common.allUnits');
  // The count only once the server has answered — never a zero while loading.
  const counted = !state.list.isLoading && !state.list.isError;

  return (
    <>
      <div className="o-page-title">
        <h1>{t('ownerApp.bookings.title')}</h1>
        <p className="o-sub" aria-live="polite">{joinList([counted ? f.bookings(state.list.total) : '', scope], language)}</p>
        <span className="o-underline" />
      </div>

      {unitList.length > 1 && <UnitPicker units={unitList} value={unitId} onChange={setUnitId} />}
      <BookingFilters state={state} searchHint={t('ownerApp.bookings.search')} />

      <section className="o-section o-section-tight">
        <BookingRows state={state} withUnit={!unitId} photoById={photoById} />
      </section>
    </>
  );
};

export default Bookings;
